import type { ReactNode } from "react";
import type { NotificationStep, ServedAction } from "@patent-studio/api-types";
import { KeptAtFoot } from "@/components/patterns/ActionBar";
import { Note } from "@/components/ui/Note";
import { NumberMark } from "@/components/ui/NumberMark";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  useNotificationWrites,
  useNotifications,
} from "@/hooks/use-notifications";
import { landOn } from "../layout/StageFooter";
import { ChangesBanner, bannerShows } from "./ChangesBanner";
import { SuggestionPanel } from "./SuggestionPanel";

/** What the attorney may do to one change: the three decisions, and whether one is on its way. */
export interface ChangeDecisions {
  pending: boolean;
  accept: () => void;
  /** Settles with the write, so the field that typed the words keeps them if it fails. */
  amend: (text: string) => Promise<unknown>;
  reject: () => void;
}

/** Where a change's record is drawn on its page, for Next change to go to. */
export function changeAnchor(actionId: string): string {
  return `ntf-${actionId}`;
}

/**
 * The step's changes while it is the one surfaced, and the decisions on them.
 * A step that is not surfaced has none to show: it is held until the earlier
 * one is reviewed (lane M decision 21).
 */
export function useStepChanges(taskId: string, step: NotificationStep) {
  const { data: view } = useNotifications(taskId);
  const writes = useNotificationWrites(taskId);
  const changes =
    view?.surfaced === step ? view.actions.filter((a) => a.step === step) : [];
  const pending =
    writes.accept.isPending ||
    writes.amend.isPending ||
    writes.reject.isPending;
  const decide = (change: ServedAction): ChangeDecisions => ({
    pending,
    accept: () => {
      writes.accept.mutate(change.action_id);
    },
    amend: (text: string) =>
      writes.amend.mutateAsync({ actionId: change.action_id, text }),
    reject: () => {
      writes.reject.mutate(change.action_id);
    },
  });
  return { view, changes, decide };
}

/**
 * A record the change would add, drawn in its list as v0.28 draws it: outlined
 * in violet, tagged "Suggested", with no number until it is accepted, its words,
 * where the new data came from, and the change's panel.
 */
export function SuggestedRow({
  change,
  decide,
  children,
}: {
  change: ServedAction;
  decide: ChangeDecisions;
  children?: ReactNode;
}) {
  return (
    <li
      id={changeAnchor(change.action_id)}
      tabIndex={-1}
      data-testid={`suggested-${change.action_id}`}
    >
      <Panel pad="snug" className="ring-2 ring-primary/60">
        <div className="flex items-start gap-2.5">
          <NumberMark number={null} label="Numbered once accepted" />
          <p className="min-w-0 flex-1 text-[13.5px] font-medium">{`"${change.after ?? ""}"`}</p>
          <StatusPill tone="brand" size="sm">
            Suggested
          </StatusPill>
        </div>
        <p className="mt-1 pl-[30px] font-mono text-[10.5px] text-muted-foreground">
          {change.source_said}
        </p>
        {children}
        <SuggestionPanel change={change} {...decide} />
      </Panel>
    </li>
  );
}

/**
 * A change to a record that stands, drawn inside the record's own row as v0.28
 * draws it. Next change lands on the panel, so the reason and the buttons are
 * in view.
 */
export function ChangeOnRecord({
  change,
  decide,
}: {
  change: ServedAction;
  decide: ChangeDecisions;
}) {
  return (
    <div id={changeAnchor(change.action_id)} tabIndex={-1}>
      <SuggestionPanel change={change} {...decide} />
    </div>
  );
}

/**
 * The step's changes to the records that stand, read by the record each one
 * changes: the panel to draw in that record's row, or nothing.
 */
export function useChangesOn(taskId: string, step: NotificationStep) {
  const { changes, decide } = useStepChanges(taskId, step);
  return (targetId: string): ReactNode => {
    const change = changes.find(
      (c) => c.change === "modify" && c.target_id === targetId,
    );
    if (change === undefined) return null;
    return <ChangeOnRecord change={change} decide={decide(change)} />;
  };
}

/** The line saying how many of the disclosure's uncited passages hold content the cap left unproposed. */
function unproposedLine(unproposed: number): string {
  return unproposed === 1
    ? "1 more passage holds content not proposed"
    : `${String(unproposed)} more passages hold content not proposed`;
}

/**
 * The elements Praxis suggests adding, at the top of the invention's list, and
 * the count of the disclosure's uncited passages that hold content it did not
 * propose: the cap is never silent, so the line stands with no suggestion left too.
 */
export function SuggestedElements({ taskId }: { taskId: string }) {
  const { view, changes, decide } = useStepChanges(taskId, "invention");
  const added = changes.filter(
    (c) => c.change === "add" && c.target_kind === "element",
  );
  const unproposed = view?.unproposed ?? 0;
  if (added.length === 0 && unproposed === 0) return null;
  return (
    <>
      {added.length > 0 && (
        <ul aria-label="Suggested elements" className="mt-4 space-y-1.5">
          {added.map((change) => (
            <SuggestedRow
              key={change.action_id}
              change={change}
              decide={decide(change)}
            />
          ))}
        </ul>
      )}
      {unproposed > 0 && (
        <Note className="mt-2">{unproposedLine(unproposed)}</Note>
      )}
    </>
  );
}

/**
 * The banner over a step's changes, or the pointer to an earlier step's,
 * sticky above the stage's footer; `alone` keeps it in view by itself on a
 * page whose footer is not drawn. `pointsOnly` is for a page that draws no
 * changes of its own, which shows the pointer alone.
 */
export function StepBanner({
  taskId,
  step,
  alone = false,
  pointsOnly = false,
}: {
  taskId: string;
  step: NotificationStep;
  alone?: boolean;
  pointsOnly?: boolean;
}) {
  const { view } = useStepChanges(taskId, step);
  if (view === undefined || !bannerShows(view, step)) return null;
  if (pointsOnly && view.surfaced === step) return null;
  const banner = (
    <ChangesBanner
      view={view}
      step={step}
      taskId={taskId}
      onJump={(actionId) => {
        landOn(changeAnchor(actionId));
      }}
    />
  );
  return alone ? <KeptAtFoot>{banner}</KeptAtFoot> : banner;
}

/** Praxis's question card in the bar's place, with the step's banner kept in view alone. */
export function CardAndBanner({
  card,
  taskId,
  step,
}: {
  card: ReactNode;
  taskId: string;
  step: NotificationStep;
}) {
  return (
    <>
      {card}
      <StepBanner taskId={taskId} step={step} alone />
    </>
  );
}
